import { SHORT_DATE_OPTIONS } from "./formatForLocale";

export type ShortDateField = "day" | "month" | "year";

export type ShortDateParts = Readonly<{
  /** The field order the locale renders, e.g. de-DE day-month-year, en-US month-day-year. */
  order: readonly [ShortDateField, ShortDateField, ShortDateField];
  /** The literal the locale puts between the fields, e.g. "." (de-DE) or "/" (en-US). */
  separator: string;
}>;

/** Order and separator to fall back on when Intl yields nothing usable. */
const FALLBACK_PARTS: ShortDateParts = {
  order: ["day", "month", "year"],
  separator: ".",
};

/**
 * Unambiguous in every field order (day 2, month 1, year 2000), so the parts
 * below can be identified by value rather than by position.
 */
const REFERENCE_DATE = new Date(2000, 0, 2);

/**
 * Direction marks Intl puts around the parts of a right-to-left date, e.g.
 * ar-EG renders "٢٤‏/١٢‏/٢٠٣٣" with a RLM before each slash.
 */
const BIDI_MARKS = /[‎‏؜⁦-⁩]/g;

/**
 * Rewrites the locale's own digits as ASCII, so a date typed the way
 * `formatShortDate` renders it parses back. Locales such as ar-EG display
 * Arabic-Indic digits; most display the ASCII ones, in which case this is a
 * no-op.
 */
function toAsciiDigits(locale: string, raw: string): string {
  let formatter: Intl.NumberFormat;
  try {
    formatter = new Intl.NumberFormat(locale, { useGrouping: false });
  } catch {
    return raw;
  }

  const digits = Array.from({ length: 10 }, (_, index) =>
    formatter.format(index),
  );
  if (digits.every((digit, index) => digit === String(index))) return raw;

  return digits.reduce(
    (text, digit, index) => text.split(digit).join(String(index)),
    raw,
  );
}

/**
 * Whether the locale counts years the way `Date` does.
 *
 * A locale like fa-IR formats in the Persian calendar, where the reference date
 * reads 1412/10/04 — numbers that mean nothing to `new Date(year, month, day)`.
 * Parsing them as Gregorian would save a date centuries off, so those locales
 * are refused outright rather than silently mistranslated.
 */
function usesGregorianCalendar(locale: string): boolean {
  try {
    return (
      new Intl.DateTimeFormat(locale).resolvedOptions().calendar === "gregory"
    );
  } catch {
    return true;
  }
}

/**
 * The field order and separator `formatShortDate` renders for `locale`.
 *
 * Derived from `Intl`, the same way `getNumberSeparatorsForLocale` derives
 * grouping separators — the pattern has to come from the locale, since
 * day-month-year ordering is itself a regional convention.
 */
export function getShortDatePartsForLocale(locale: string): ShortDateParts {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat(locale, SHORT_DATE_OPTIONS).formatToParts(
      REFERENCE_DATE,
    );
  } catch {
    return FALLBACK_PARTS;
  }

  const order = parts
    .map((part) => part.type)
    .filter((type): type is ShortDateField =>
      ["day", "month", "year"].includes(type),
    );
  const separator = parts
    .find((part) => part.type === "literal" && part.value.trim() !== "")
    ?.value.replaceAll(BIDI_MARKS, "")
    .trim();

  if (order.length !== 3) return FALLBACK_PARTS;

  return {
    order: [order[0], order[1], order[2]] as ShortDateParts["order"],
    separator: separator || FALLBACK_PARTS.separator,
  };
}

/** Two-digit years land in the current century: "33" is 2033, "99" is 2099. */
function expandTwoDigitYear(year: number): number {
  return 2000 + year;
}

/**
 * Builds a Date only when every field survives the round trip. `new Date(…)`
 * rolls impossible dates over (31 February becomes 2 or 3 March), which would
 * silently save a date the user never typed.
 */
function buildStrictDate(
  day: number,
  month: number,
  year: number,
): Date | null {
  const candidate = new Date(year, month - 1, day);
  if (
    candidate.getFullYear() !== year ||
    candidate.getMonth() !== month - 1 ||
    candidate.getDate() !== day
  ) {
    return null;
  }
  return candidate;
}

function assignFields(
  order: ShortDateParts["order"],
  values: readonly [number, number, number],
  yearDigits: number,
): Date | null {
  const [first, second, third] = values;
  const fields: Record<ShortDateField, number> = { day: 0, month: 0, year: 0 };
  fields[order[0]] = first;
  fields[order[1]] = second;
  fields[order[2]] = third;

  const year = yearDigits === 2 ? expandTwoDigitYear(fields.year) : fields.year;
  return buildStrictDate(fields.day, fields.month, year);
}

function escapeForRegExp(value: string): string {
  return value.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
}

/**
 * Parses a short date the user typed, in the field order `locale` displays.
 *
 * Separators are lenient — the locale's own separator plus `.`, `-`, `/` and
 * whitespace all work, because keyboards and habits vary. The *order* is not:
 * it always follows the locale, which is what the placeholder announces. So
 * "05.01.2033" is 1 May under de-DE and 5 January under en-US, and a date that
 * cannot exist in the locale's order (de-DE "12/24/2033" → month 24) is
 * rejected rather than reinterpreted.
 *
 * Returns null for anything that is not a complete, possible date, so
 * half-typed input never overwrites a committed value — including every date
 * in a locale that does not count years the Gregorian way.
 */
export function parseShortDateForLocale(
  locale: string,
  raw: string,
): Date | null {
  if (!usesGregorianCalendar(locale)) return null;

  const trimmed = toAsciiDigits(locale, raw.replaceAll(BIDI_MARKS, "")).trim();
  if (!trimmed) return null;

  const { order, separator } = getShortDatePartsForLocale(locale);

  // Fixed-width digits ("24122033") — how a numpad user types a date.
  if (/^\d{8}$/.test(trimmed)) {
    const isYearFirst = order[0] === "year";
    const values: [number, number, number] = isYearFirst
      ? [
          Number(trimmed.slice(0, 4)),
          Number(trimmed.slice(4, 6)),
          Number(trimmed.slice(6, 8)),
        ]
      : [
          Number(trimmed.slice(0, 2)),
          Number(trimmed.slice(2, 4)),
          Number(trimmed.slice(4, 8)),
        ];
    return assignFields(order, values, 4);
  }

  const separatorClass = String.raw`[${escapeForRegExp(separator)}./\-\s]+`;
  const tokens = trimmed.split(new RegExp(separatorClass));
  if (tokens.length !== 3) return null;

  const yearIndex = order.indexOf("year");
  const values: number[] = [];
  let yearDigits = 0;
  for (const [index, token] of tokens.entries()) {
    if (!/^\d+$/.test(token)) return null;
    // A year is written with two or four digits; day and month with one or two.
    const allowed = index === yearIndex ? [2, 4] : [1, 2];
    if (!allowed.includes(token.length)) return null;
    if (index === yearIndex) yearDigits = token.length;
    values.push(Number(token));
  }

  const [first, second, third] = values as [number, number, number];
  return assignFields(order, [first, second, third], yearDigits);
}

/**
 * A placeholder that matches what `parseShortDateForLocale` accepts and
 * `formatShortDate` renders — "TT.MM.JJJJ" in German, "MM/DD/YYYY" in
 * American English.
 *
 * Order and separator come from the locale; the letters have to be supplied by
 * the caller because they are words ("Tag"/"day"), not data Intl carries.
 */
export function getShortDatePlaceholderForLocale(
  locale: string,
  letters: Readonly<{ day: string; month: string; year: string }>,
): string {
  const { order, separator } = getShortDatePartsForLocale(locale);
  return order
    .map((field) => letters[field].repeat(field === "year" ? 4 : 2))
    .join(separator);
}
